import React, { useEffect, useMemo, useState } from 'react';
import { Drawer, Form, Input, InputNumber, Row, Col, Button, Select, DatePicker, Typography, Space, Divider, Spin } from 'antd';
import { DollarOutlined, TagOutlined, UserOutlined, CalendarOutlined, FileTextOutlined, AppstoreOutlined, LoadingOutlined } from '@ant-design/icons';
import { useSelector, useDispatch } from 'react-redux';
import { categoryActions } from '../../actions/categoryActions';
import { getServiceParams } from '../../actions/commonActions';
import { userActions } from '../../actions/userActions';
import { axiosBase } from '../../services/axios';
import { Option } from 'antd/es/mentions';

const { TextArea } = Input;
const { Text } = Typography;

export interface ExpenseInput {
    _id?: string;
    categoryId: string;
    serviceId: string;
    studentId?: string;
    expenseName: string;
    description?: string;
    amount: number;
    expenseDate: string;
}

interface ExpenseFormModalProps {
    open: boolean;
    title: string;
    onCancel: () => void;
    onFinish: (values: ExpenseInput) => void;
    form: any;
    isLoading?: boolean;   // ← add
}

const ExpenseFormModal: React.FC<ExpenseFormModalProps> = ({
    open,
    title,
    onCancel,
    onFinish,
    form,
    isLoading,   // ← add
}) => {
    const dispatch = useDispatch();
    const categories = useSelector((state: any) => state.getCategoryParamsReducer?.categorydata);
    const services = useSelector((state: any) => state.getServiceParamsReducer?.servicedata);
    const students = useSelector((state: any) => state.userListReducer);
    const roleListData = useSelector((state: any) => state.roleDataReducer);
    const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
    const [loadingStudentData, setLoadingStudentData] = useState(false);
    const [autoPopulated, setAutoPopulated] = useState(false);



    useEffect(() => {
        if (open) {
            dispatch(categoryActions.getCategoryParams({ pageNumber: 1, pageSize: 100 }));
            dispatch(getServiceParams({ pageNumber: 1, pageSize: 100 }));
            dispatch(userActions.userRoleRequest());
        }
    }, [open, dispatch]);

    useEffect(() => {
        if (roleListData && roleListData.roleData) {
            const studentRole = roleListData.roleData.find((r: any) => r.roleName === 'Student');
            if (studentRole) {
                dispatch(userActions.userListRequest({ role: studentRole._id }));
            }
        }
    }, [roleListData, dispatch]);

    const allServices = useMemo(() => {
        const payload = services?.services ?? services?.data ?? services ?? [];
        return Array.isArray(payload) ? payload : [];
    }, [services]);

    const allCategories = useMemo(() => {
        const payload = categories?.categories ?? categories?.data ?? categories ?? [];
        return Array.isArray(payload) ? payload : [];
    }, [categories]);

    const categoryOptions = useMemo(() => {
        const payload = categories?.categories;
        const list = payload?.categories ?? payload ?? [];
        const filtered = (Array.isArray(list) ? list : []).filter((c: any) => c.categoryType !== 'QUERY');
        return filtered;
    }, [categories]);
    // const categoryOptions = useMemo(() => {
    //     return allCategories.map((s: any) => ({
    //         label: s?.categoryName,
    //         value: s?._id,
    //     }));
    // }, [allCategories]);

    const serviceOptions = useMemo(() => {
        if (selectedCategoryId) {
            const filteredServices = allServices.filter((s: any) => {
                const serviceCategoryId = s?.category?._id || s?.categoryId || s?.category || s?.categoryId?._id;
                return String(serviceCategoryId) === String(selectedCategoryId);
            });
            return filteredServices.map((c: any) => ({
                label: c?.serviceName,
                value: c?._id,
            }));
        }
        return [];
    }, [allCategories, allServices, selectedCategoryId]);

    const studentOptions = useMemo(() => {
        const users = students?.userList?.users ?? [];
        return (Array.isArray(users) ? users : []).map((s: any) => {
            const name = `${s?.firstName || ''} ${s?.lastName || ''}`.trim();
            const studentId = s?.enrolledNumber || s?.studentId;
            const displayLabel = studentId ? `${name} (${studentId})` : name;
            return {
                label: displayLabel,
                value: String(s?._id),
                key: String(s?._id),
                enrolledNumber: studentId,
            };
        });
    }, [students]);

    const handleStudentChange = async (studentId: string) => {
        if (!studentId) {
            setAutoPopulated(false);
            return;
        }
        setLoadingStudentData(true);
        try {
            const response = await axiosBase.get(`/users/student/${studentId}/enrollment`);
            if (response.data?.success && response.data?.resultObject) {
                const enrollmentData = response.data.resultObject;
                setSelectedCategoryId(enrollmentData.categoryId);
                form.setFieldsValue({ categoryId: enrollmentData.categoryId, serviceId: enrollmentData.serviceId });
                setAutoPopulated(true);
            }
        } catch (error: any) {
            setAutoPopulated(false);
        } finally {
            setLoadingStudentData(false);
        }
    };

    const handleFinish = (values: any) => {
        const payload = {
            categoryId: values.categoryId,
            serviceId: values.serviceId,
            studentId: values.studentId || null,
            expenseName: values.expenseName,
            description: values.description || '',
            amount: values.amount,
            expenseDate: values.expenseDate?.format('YYYY-MM-DD'),
        };
        onFinish(payload as ExpenseInput);
    };

    const handleCancel = () => {
        setSelectedCategoryId(null);
        setAutoPopulated(false);
        form.resetFields();
        onCancel();
    };

    return (
        <Drawer
            className="exp-drawer"
            open={open}
            onClose={handleCancel}
            width={window.innerWidth > 1400 ? 530 : 480}
            closable={false}
            destroyOnClose
            title={null}
            footer={
                <div className="exp-drawer-footer">
                    <Button className="exp-btn-cancel" onClick={handleCancel} disabled={!!isLoading}>
                        Cancel
                    </Button>
                    <Button
                        className="exp-btn-save"
                        type="primary"
                        onClick={() => form.submit()}
                        loading={!!isLoading}
                        disabled={!!isLoading}
                    >
                        {isLoading ? 'Saving...' : 'Save Expense'}
                    </Button>
                </div>
            }
        >
            {/* ── Spinner Overlay ── */}
            {isLoading && (
                <div className="svc-spinner-overlay">
                    <Spin
                        indicator={<LoadingOutlined style={{ fontSize: 40, color: 'var(--primary-color)' }} spin />}
                    />
                    <span style={{ color: 'var(--primary-color)', fontWeight: 500, fontSize: 14 }}>
                        Saving expense...
                    </span>
                </div>
            )}

            {/* ── Sticky Header ── */}
            <div className="exp-drawer-header">
                <div className="exp-drawer-header-inner">
                    <div className="exp-drawer-title-group">
                        <div className="exp-drawer-icon-box">
                            <DollarOutlined />
                        </div>
                        <div className="exp-drawer-title-text">
                            <span className="exp-drawer-title">{title}</span>
                            <span className="exp-drawer-subtitle">Fill in the details to record an expense entry</span>
                        </div>
                    </div>
                    <button className="exp-drawer-close-btn" onClick={handleCancel} aria-label="Close">
                        ✕
                    </button>
                </div>
                <Divider className="exp-drawer-header-divider" />
            </div>

            <Form form={form} layout="vertical" onFinish={handleFinish} className="exp-form">

                {/* ── Section: Basic Information ── */}
                <div className="exp-form-section">

                    <Form.Item
                        name="expenseName"
                        label="Expense Name"
                        rules={[{ required: true, message: 'Please enter expense name' }]}
                        className="exp-form-item"
                    >
                        <Input
                            className="exp-input"
                            placeholder="e.g. Fuel, Maintenance, Office Supplies"
                            prefix={<FileTextOutlined className="exp-input-prefix-icon" />}
                        />
                    </Form.Item>

                    <Form.Item
                        name="studentId"
                        label={
                            <span className="exp-label">
                                Student

                            </span>
                        }
                        className="exp-form-item"
                    >
                        <Select
                            className="exp-select"
                            placeholder="Search and select a student"
                            showSearch
                            allowClear
                            loading={loadingStudentData}
                            listHeight={200}
                            optionFilterProp="label"
                            filterOption={(input, option: any) =>
                                String(option?.label || '').toLowerCase().includes(input.toLowerCase())
                            }
                            options={studentOptions}
                            suffixIcon={<UserOutlined className="exp-select-suffix" />}
                            onChange={(value) => {
                                if (value) {
                                    handleStudentChange(value);
                                } else {
                                    setAutoPopulated(false);
                                }
                            }}
                        />
                    </Form.Item>

                  
                </div>



                {/* ── Section: Classification ── */}
                <div className="exp-form-section">


                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="categoryId"
                                label="Category"
                                className="exp-form-item"
                            >
                                <Select
                                     className="exp-select"
                                    placeholder="Select category"
                                    onChange={(value) => {
                                        form.setFieldsValue({ serviceId: undefined });
                                        setSelectedCategoryId(value);
                                    }}
                                >
                                    {categoryOptions.map((r: any) => (
                                        <Option key={r._id} value={r._id}>{r.categoryName}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="serviceId"
                                label="Service"
                                className="exp-form-item"
                            >
                                <Select
                                    className="exp-select"
                                    placeholder={selectedCategoryId ? 'Select service' : 'Select category first'}
                                    showSearch
                                    listHeight={200}
                                    disabled={!selectedCategoryId}
                                    filterOption={(input, option: any) =>
                                        option?.label?.toLowerCase().includes(input.toLowerCase())
                                    }
                                    options={serviceOptions}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Divider className="exp-section-divider" />
                </div>



                {/* ── Section: Payment Details ── */}
                <div className="exp-form-section">
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="amount"
                                label="Amount (₹)"
                                rules={[
                                    { required: true, message: 'Please enter amount' },
                                    { type: 'number', min: 0, message: 'Amount must be positive' },
                                ]}
                                className="exp-form-item"
                            >
                                <InputNumber
                                    className="exp-input-number"
                                    placeholder="0.00"
                                    prefix="₹"
                                    min={0}
                                    precision={2}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="expenseDate"
                                label="Expense Date"
                                rules={[{ required: true, message: 'Please select expense date' }]}
                                className="exp-form-item"
                            >
                                <DatePicker
                                    className="exp-datepicker"
                                    placeholder="DD-MM-YYYY"
                                    format="DD-MM-YYYY"
                                    suffixIcon={<CalendarOutlined className="exp-datepicker-icon" />}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                    {/* <Divider className="exp-section-divider" /> */}
                </div>



                {/* ── Section: Notes ── */}
                <div className="exp-form-section exp-form-section--last">


                    <Form.Item
                        name="description"
                        label={
                            <span className="exp-label">
                                Description
                            </span>
                        }
                        className="exp-form-item"
                    >
                        <TextArea
                            className="exp-textarea"
                            rows={3}
                            autoSize={{ minRows: 3, maxRows: 5 }}
                            placeholder="Add any relevant notes or remarks about this expense…"
                            maxLength={500}
                            showCount
                        />
                    </Form.Item>
                </div>

            </Form>
        </Drawer>
    );
};

export default ExpenseFormModal;
